import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { fmtHrs, fmtMins, fmtTime, fmtDay, addDays, jobLabel, toLocalInput, JobSelect } from './TimeClockField';

const card = { background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--r2)' };
const th = { padding: '8px 10px', fontSize: 10, fontWeight: 600, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.07em', textAlign: 'right', whiteSpace: 'nowrap', borderBottom: '1px solid var(--border)' };
const td = { padding: '9px 10px', fontSize: 12, textAlign: 'right', whiteSpace: 'nowrap', borderBottom: '1px solid var(--border)' };
const dec = (h) => (Number(h) || 0).toFixed(2);
const mapLink = (lat, lng) => lat != null && lng != null ? `https://maps.google.com/?q=${lat},${lng}` : null;
const sevenDays = (start) => Array.from({ length: 7 }, (_, i) => addDays(start, i));

export default function TimeclockAdmin({ jobs, onPendingChange }) {
  const [tab, setTab] = useState('sheet');
  const [pending, setPending] = useState(0);
  const [health, setHealth] = useState(null);

  const refreshPending = useCallback(async () => {
    try {
      const r = await api.get('/time/admin/pending-count');
      setPending(r.data.count);
      onPendingChange && onPendingChange(r.data.count);
    } catch (e) { /* ignore */ }
  }, [onPendingChange]);

  useEffect(() => { refreshPending(); }, [refreshPending]);
  useEffect(() => { api.get('/time/admin/health').then(r => setHealth(r.data)).catch(() => {}); }, []);

  const jobOptions = (jobs || []).filter(j => j.status !== 'completed')
    .map(j => ({ id: j.id, title: j.title, job_number: j.job_number, assigned: false }));

  return (
    <div>
      {/* A one-hour clock error would be silent otherwise */}
      {health && health.ok === false && (
        <div style={{ background: 'rgba(231,76,60,.08)', border: '1px solid rgba(231,76,60,.3)', borderRadius: 'var(--r)', padding: '10px 13px', display: 'flex', gap: 9, marginBottom: 12 }}>
          <i className="fa fa-triangle-exclamation" style={{ color: 'var(--red2)', marginTop: 2 }}></i>
          <div style={{ fontSize: 12, color: 'var(--red2)' }}>
            <div style={{ fontWeight: 600, marginBottom: 2 }}>Server clock is out of date — hours may be an hour off</div>
            {health.warning}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 4, marginBottom: 14 }}>
        {[['sheet', 'fa-table', 'Timesheets'], ['fixes', 'fa-pen-to-square', 'Fix requests'], ['settings', 'fa-sliders', 'Settings']].map(([id, icon, l]) => (
          <div key={id} onClick={() => setTab(id)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 7, cursor: 'pointer', fontSize: 12,
              color: tab === id ? 'var(--blue2)' : 'var(--tx2)', background: tab === id ? 'rgba(58,123,213,.15)' : 'transparent',
              border: `1px solid ${tab === id ? 'rgba(58,123,213,.3)' : 'transparent'}` }}>
            <i className={`fa ${icon}`}></i> {l}
            {id === 'fixes' && pending > 0 && <span className="nbadge nb-a">{pending}</span>}
          </div>
        ))}
      </div>
      {tab === 'sheet' && <Timesheet jobs={jobOptions} onChanged={refreshPending} onOpenFixes={() => setTab('fixes')} />}
      {tab === 'fixes' && <FixRequests onChanged={refreshPending} />}
      {tab === 'settings' && <Settings health={health} />}
    </div>
  );
}

// ── TIMESHEETS ────────────────────────────────────────────────
function Timesheet({ jobs, onChanged, onOpenFixes }) {
  const [mode, setMode] = useState('period');   // period | week | custom
  const [weeks, setWeeks] = useState(null);     // null = use the saved setting
  const [anchor, setAnchor] = useState(null);   // a date inside the period/week
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [userId, setUserId] = useState('');

  const [data, setData] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [edit, setEdit] = useState(null);       // { punch } or { userId }
  const [exporting, setExporting] = useState('');
  const [err, setErr] = useState('');

  const params = useCallback(() => {
    const p = {};
    if (mode === 'custom') {
      if (!from || !to) return null;
      p.from = from; p.to = to;
    } else if (mode === 'week') {
      p.weeks = 1;
      if (anchor) p.weekStart = anchor;
    } else {
      if (weeks) p.weeks = weeks;
      if (anchor) p.period = anchor;
    }
    if (userId) p.userId = userId;
    return p;
  }, [mode, weeks, anchor, from, to, userId]);

  const load = useCallback(async () => {
    const p = params();
    if (p === null) { setErr('Pick both a start and end date'); return; }
    setErr('');
    try {
      const r = await api.get('/time/admin/range', { params: p });
      setData(r.data);
      if (!weeks && r.data.range.weeks) setWeeks(r.data.range.weeks);
    } catch (e) { setErr(e.response?.data?.error || 'Could not load timesheets'); }
  }, [params, weeks]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/time/admin/workers').then(r => setWorkers(r.data)).catch(() => {}); }, []);

  async function exportCsv() {
    const p = params();
    if (p === null) { setErr('Pick both a start and end date'); return; }
    setExporting('csv');
    try {
      const r = await api.get('/time/admin/export.csv', { params: p, responseType: 'blob' });
      const cd = r.headers?.['content-disposition'] || '';
      const m = /filename="?([^"]+)"?/.exec(cd);
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = m ? m[1] : `timesheet-${data?.range.start || 'export'}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { alert('Export failed'); }
    finally { setExporting(''); }
  }

  async function del(p) {
    if (!window.confirm(`Delete this punch (${fmtTime(p.clock_in)} – ${p.open ? 'open' : fmtTime(p.clock_out)})?`)) return;
    try { await api.delete(`/time/admin/punches/${p.id}`); load(); }
    catch (e) { alert(e.response?.data?.error || 'Delete failed'); }
  }

  if (err && !data) return <div className="empty"><i className="fa fa-exclamation-circle"></i>{err}</div>;
  if (!data) return <div className="empty"><div className="spin" style={{ margin: '0 auto 12px' }}></div>Loading…</div>;

  const range = data.range;
  const gps = !!data.settings.gps_enabled;
  const singleWeek = range.weeks === 1 && range.mode !== 'custom';
  const dayCols = singleWeek ? sevenDays(range.start) : null;
  const colCount = singleWeek ? 12 : 6;
  const g = data.grand;
  const label = `${fmtDay(range.start)} – ${fmtDay(range.end)}`;

  return (
    <div>
      {/* Range + filter bar */}
      <div style={{ ...card, padding: '12px 14px', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', marginBottom: 10 }}>
          {[['period', 'fa-calendar-days', 'Pay period'], ['week', 'fa-calendar-week', 'One week'], ['custom', 'fa-calendar-day', 'Date range']].map(([id, icon, l]) => (
            <div key={id} onClick={() => {
                // Seed the date boxes from whatever is on screen, so switching
                // to Date range doesn't land on an empty form.
                if (id === 'custom' && !from && data) { setFrom(data.range.start); setTo(data.range.end); }
                setMode(id); setAnchor(null);
              }}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 7, cursor: 'pointer', fontSize: 12,
                color: mode === id ? 'var(--blue2)' : 'var(--tx2)', background: mode === id ? 'rgba(58,123,213,.15)' : 'transparent',
                border: `1px solid ${mode === id ? 'rgba(58,123,213,.3)' : 'var(--border)'}` }}>
              <i className={`fa ${icon}`}></i> {l}
            </div>
          ))}
          {mode === 'period' && (
            <select className="fs" value={weeks || 2} onChange={e => { setWeeks(Number(e.target.value)); setAnchor(null); }}>
              {[1, 2, 3, 4].map(n => <option key={n} value={n}>{n} week{n > 1 ? 's' : ''}</option>)}
            </select>
          )}
          <div style={{ flex: 1 }}></div>
          <select className="fs" value={userId} onChange={e => setUserId(e.target.value)} style={{ minWidth: 160 }}>
            <option value="">All workers</option>
            {workers.map(w => <option key={w.id} value={w.id}>{w.name}{w.active === false ? ' (inactive)' : ''}</option>)}
          </select>
        </div>

        {mode === 'custom' ? (
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 9, flexWrap: 'wrap' }}>
            <div className="fg" style={{ marginBottom: 0 }}><label className="fl">From</label>
              <input type="date" className="fi" value={from} onChange={e => setFrom(e.target.value)} style={{ width: 155 }} /></div>
            <div className="fg" style={{ marginBottom: 0 }}><label className="fl">To</label>
              <input type="date" className="fi" value={to} onChange={e => setTo(e.target.value)} style={{ width: 155 }} /></div>
            <button className="btn btn-blue btn-sm" onClick={load}><i className="fa fa-magnifying-glass"></i> Apply</button>
            <div style={{ flex: 1 }}></div>
            <button className="btn btn-sm" onClick={() => setEdit({ userId: data.employees[0]?.user.id || null })}><i className="fa fa-plus"></i> Add punch</button>
            <button className="btn btn-blue btn-sm" onClick={exportCsv} disabled={!!exporting}>
              {exporting ? <div className="spin" style={{ width: 12, height: 12, borderWidth: 2 }}></div> : <i className="fa fa-file-csv"></i>} Export CSV
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn btn-sm" onClick={() => setAnchor(range.prev)}><i className="fa fa-chevron-left"></i></button>
            <div style={{ fontSize: 13, fontWeight: 600, minWidth: 190, textAlign: 'center' }}>
              {label}
              <div style={{ fontSize: 10, fontWeight: 400, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.07em' }}>
                {range.mode === 'period' ? `${range.weeks}-week pay period` : 'Week'}
              </div>
            </div>
            <button className="btn btn-sm" onClick={() => setAnchor(range.next)}><i className="fa fa-chevron-right"></i></button>
            <button className="btn btn-sm" onClick={() => setAnchor(null)}>Current</button>
            <div style={{ flex: 1 }}></div>
            <button className="btn btn-sm" onClick={() => setEdit({ userId: data.employees[0]?.user.id || null })}><i className="fa fa-plus"></i> Add punch</button>
            <button className="btn btn-blue btn-sm" onClick={exportCsv} disabled={!!exporting}>
              {exporting ? <div className="spin" style={{ width: 12, height: 12, borderWidth: 2 }}></div> : <i className="fa fa-file-csv"></i>} Export CSV
            </button>
          </div>
        )}
      </div>

      {err && <div style={{ fontSize: 12, color: 'var(--red2)', padding: '8px 11px', background: 'rgba(231,76,60,.1)', borderRadius: 'var(--r)', marginBottom: 10 }}><i className="fa fa-exclamation-circle"></i> {err}</div>}

      {range.weekAligned === false && (
        <div style={{ background: 'rgba(232,160,32,.08)', border: '1px solid rgba(232,160,32,.25)', borderRadius: 'var(--r)', padding: '8px 12px', fontSize: 12, color: 'var(--amber2)', marginBottom: 10 }}>
          <i className="fa fa-circle-info"></i> This range doesn't start and end on whole weeks. Overtime is a weekly rule, so the OT column may be understated — use a pay period or a full week for payroll.
        </div>
      )}
      {data.pendingCorrections > 0 && (
        <div onClick={onOpenFixes} style={{ cursor: 'pointer', background: 'rgba(232,160,32,.08)', border: '1px solid rgba(232,160,32,.25)', borderRadius: 'var(--r)', padding: '8px 12px', fontSize: 12, color: 'var(--amber2)', marginBottom: 10 }}>
          <i className="fa fa-pen-to-square"></i> {data.pendingCorrections} fix request{data.pendingCorrections > 1 ? 's' : ''} waiting for approval — review them before exporting
        </div>
      )}
      {g.openPunches > 0 && (
        <div style={{ background: 'rgba(58,123,213,.08)', border: '1px solid rgba(58,123,213,.25)', borderRadius: 'var(--r)', padding: '8px 12px', fontSize: 12, color: 'var(--blue2)', marginBottom: 10 }}>
          <i className="fa fa-circle-info"></i> {g.openPunches} worker{g.openPunches > 1 ? 's are' : ' is'} still clocked in. Open punches are left out of the CSV export.
        </div>
      )}

      {/* Totals strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8, marginBottom: 12 }}>
        {[
          ['Paid hours', dec(g.total), 'var(--tx)'],
          ['Regular', dec(g.regular), 'var(--blue2)'],
          ['Overtime', dec(g.overtime), g.overtime > 0 ? 'var(--amber2)' : 'var(--tx3)'],
          ['Unpaid breaks', fmtMins(g.breakMinutes), 'var(--tx3)'],
        ].map(([l, v, c]) => (
          <div key={l} style={{ ...card, padding: '11px 13px' }}>
            <div style={{ fontSize: 10, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 4 }}>{l}</div>
            <div style={{ fontSize: 18, fontWeight: 600, color: c, fontFamily: "'DM Mono',monospace" }}>{v}</div>
          </div>
        ))}
      </div>

      {/* Main table */}
      <div style={{ ...card, overflowX: 'auto', marginBottom: 14 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...th, textAlign: 'left' }}>Employee</th>
              {singleWeek && dayCols.map(d => <th key={d} style={th}>{fmtDay(d).split(',')[0]}</th>)}
              <th style={th}>Break</th>
              <th style={th}>Paid</th><th style={th}>Reg</th><th style={th}>OT</th>
            </tr>
          </thead>
          <tbody>
            {data.employees.length === 0 && (
              <tr><td colSpan={colCount} style={{ ...td, textAlign: 'center', color: 'var(--tx3)' }}>
                No hourly in-house staff yet. Set a worker to In-house, Per hour in Crews &amp; Subs.
              </td></tr>
            )}
            {data.employees.map(e => {
              const open = e.punches.some(p => p.open);
              const long = e.punches.some(p => p.longShift);
              const isOpen = !!expanded[e.user.id];
              return (
                <React.Fragment key={e.user.id}>
                  <tr style={{ cursor: 'pointer' }} onClick={() => setExpanded(x => ({ ...x, [e.user.id]: !x[e.user.id] }))}>
                    <td style={{ ...td, textAlign: 'left' }}>
                      <i className={`fa fa-chevron-${isOpen ? 'down' : 'right'}`} style={{ fontSize: 9, color: 'var(--tx3)', marginRight: 7 }}></i>
                      <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: e.user.color || '#3A7BD5', marginRight: 7 }}></span>
                      <span style={{ fontWeight: 600 }}>{e.user.name}</span>
                      {open && <span className="tag tag-green" style={{ fontSize: 9, marginLeft: 7 }}>On clock</span>}
                      {long && <span className="tag tag-red" style={{ fontSize: 9, marginLeft: 5 }} title="Open more than 12 hours — forgot to clock out?">12h+ open</span>}
                    </td>
                    {singleWeek && dayCols.map(d => <td key={d} style={{ ...td, color: e.days[d] ? 'var(--tx)' : 'var(--tx3)' }}>{e.days[d] ? dec(e.days[d]) : '–'}</td>)}
                    <td style={{ ...td, color: e.breakMinutes > 0 ? 'var(--tx2)' : 'var(--tx3)' }}>{e.breakMinutes > 0 ? fmtMins(e.breakMinutes) : '–'}</td>
                    <td style={{ ...td, fontWeight: 600 }}>{dec(e.total)}</td>
                    <td style={td}>{dec(e.regular)}</td>
                    <td style={{ ...td, color: e.overtime > 0 ? 'var(--amber2)' : 'var(--tx3)', fontWeight: e.overtime > 0 ? 600 : 400 }}>{dec(e.overtime)}</td>
                  </tr>

                  {isOpen && (
                    <tr><td colSpan={colCount} style={{ padding: '6px 10px 12px 30px', borderBottom: '1px solid var(--border)', background: 'var(--bg3)' }}>
                      {/* Per-week subtotals, so the OT split is visible */}
                      {e.weeks.length > 1 && (
                        <div style={{ marginBottom: 10 }}>
                          {e.weeks.map(w => (
                            <div key={w.weekStart} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 11, padding: '4px 0', color: 'var(--tx2)' }}>
                              <div style={{ width: 150, fontWeight: 600 }}>Week of {fmtDay(w.weekStart)}</div>
                              <div style={{ fontFamily: "'DM Mono',monospace" }}>
                                {dec(w.regular)} reg
                                {w.overtime > 0 && <span style={{ color: 'var(--amber2)' }}> + {dec(w.overtime)} OT</span>}
                                {' = '}<strong>{dec(w.total)}</strong>
                                {w.breakMinutes > 0 && <span style={{ color: 'var(--tx3)' }}> · {fmtMins(w.breakMinutes)} break</span>}
                              </div>
                            </div>
                          ))}
                          <div style={{ height: 1, background: 'var(--border)', margin: '7px 0' }}></div>
                        </div>
                      )}

                      {e.punches.length === 0 && <div style={{ fontSize: 12, color: 'var(--tx3)', padding: '6px 0' }}>No punches in this range</div>}
                      {e.punches.map(p => (
                        <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px dashed var(--border)', fontSize: 12, flexWrap: 'wrap' }}>
                          <div style={{ width: 92, color: 'var(--tx2)' }}>{fmtDay(p.work_date)}</div>
                          <div style={{ width: 190, whiteSpace: 'nowrap', fontFamily: "'DM Mono',monospace" }}>
                            {fmtTime(p.clock_in)} – {p.open ? <span style={{ color: p.on_break ? 'var(--amber2)' : 'var(--green2)' }}>{p.on_break ? 'on break' : 'now'}</span> : fmtTime(p.clock_out)}
                          </div>
                          <div style={{ width: 60, textAlign: 'right' }} title={`${dec(p.grossHours)} on site`}>{dec(p.hours)}</div>
                          <div style={{ width: 56, textAlign: 'right', color: p.breakMinutes > 0 ? 'var(--amber2)' : 'var(--tx3)' }}>
                            {p.breakMinutes > 0 ? `−${Math.round(p.breakMinutes)}m` : '–'}
                          </div>
                          <div style={{ flex: 1, minWidth: 140 }}>
                            {jobLabel(p)}
                            {p.overtime > 0 && <span className="tag tag-amber" style={{ fontSize: 9, marginLeft: 6 }}>{dec(p.overtime)} OT</span>}
                            {p.break_source && p.break_source !== 'timer' && (
                              <span className="tag tag-gray" style={{ fontSize: 9, marginLeft: 6 }}
                                title={p.break_source === 'manual' ? 'Break minutes typed at clock-out' : p.break_source === 'auto' ? 'Automatic deduction' : 'Break set by the office'}>
                                break: {p.break_source}
                              </span>
                            )}
                            {p.source !== 'clock' && <span className="tag tag-gray" style={{ fontSize: 9, marginLeft: 6 }} title={p.note}>{p.source === 'admin' ? 'added by office' : p.source === 'correction' ? 'missed punch' : 'edited'}</span>}
                            {p.source === 'clock' && p.edited_at && <span className="tag tag-gray" style={{ fontSize: 9, marginLeft: 6 }} title={p.note}>edited</span>}
                          </div>
                          {gps && (
                            <div style={{ display: 'flex', gap: 8, fontSize: 11 }}>
                              {mapLink(p.in_lat, p.in_lng)
                                ? <a href={mapLink(p.in_lat, p.in_lng)} target="_blank" rel="noreferrer" style={{ color: 'var(--blue2)' }}><i className="fa fa-location-dot"></i> In</a>
                                : p.source === 'clock' && <span style={{ color: 'var(--tx3)' }} title="No location captured"><i className="fa fa-location-dot"></i> In ?</span>}
                              {mapLink(p.out_lat, p.out_lng)
                                ? <a href={mapLink(p.out_lat, p.out_lng)} target="_blank" rel="noreferrer" style={{ color: 'var(--blue2)' }}><i className="fa fa-location-dot"></i> Out</a>
                                : p.source === 'clock' && !p.open && <span style={{ color: 'var(--tx3)' }} title="No location captured"><i className="fa fa-location-dot"></i> Out ?</span>}
                            </div>
                          )}
                          <button className="btn btn-sm" onClick={() => setEdit({ punch: p })}><i className="fa fa-pen"></i></button>
                          <button className="btn btn-sm" style={{ color: 'var(--red2)' }} onClick={() => del(p)}><i className="fa fa-trash"></i></button>
                        </div>
                      ))}
                      <button className="btn btn-sm" style={{ marginTop: 8 }} onClick={() => setEdit({ userId: e.user.id })}><i className="fa fa-plus"></i> Add punch for {e.user.name.split(' ')[0]}</button>
                    </td></tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
          {data.employees.length > 1 && (
            <tfoot>
              <tr>
                <td style={{ ...td, textAlign: 'left', fontWeight: 600, borderBottom: 'none' }}>All staff</td>
                {singleWeek && dayCols.map(d => <td key={d} style={{ ...td, borderBottom: 'none' }}></td>)}
                <td style={{ ...td, borderBottom: 'none', color: 'var(--tx3)' }}>{g.breakMinutes > 0 ? fmtMins(g.breakMinutes) : '–'}</td>
                <td style={{ ...td, fontWeight: 600, borderBottom: 'none' }}>{dec(g.total)}</td>
                <td style={{ ...td, borderBottom: 'none' }}>{dec(g.regular)}</td>
                <td style={{ ...td, borderBottom: 'none', color: g.overtime > 0 ? 'var(--amber2)' : 'var(--tx3)' }}>{dec(g.overtime)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <div style={{ fontSize: 11, color: 'var(--tx3)' }}>
        Hours in decimals, after unpaid breaks. Overtime counts after {data.otThreshold || 40} hours <strong>per week</strong> — over a multi-week period each week is figured separately and added up. Click a name to see and edit punches.
      </div>

      {edit && (
        <PunchModal punch={edit.punch} userId={edit.userId} employees={data.employees.map(e => e.user)} jobs={jobs}
          maxBreak={Number(data.settings.break_max_minutes) || 480}
          onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); onChanged && onChanged(); }} />
      )}
    </div>
  );
}

// ── ADD / EDIT PUNCH ──────────────────────────────────────────
function PunchModal({ punch, userId, employees, jobs, maxBreak, onClose, onSaved }) {
  const [worker, setWorker] = useState(punch ? punch.user_id : userId || '');
  const [jobId, setJobId] = useState(punch ? punch.job_id || null : null);
  const [start, setStart] = useState(punch ? toLocalInput(punch.clock_in) : '');
  const [end, setEnd] = useState(punch && punch.clock_out ? toLocalInput(punch.clock_out) : '');
  const [brk, setBrk] = useState(punch ? String(Math.round(Number(punch.breakMinutes) || 0)) : '0');
  const [note, setNote] = useState(punch?.note || '');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const isOpen = punch && !punch.clock_out;

  // Make sure the punch's own job shows even if that job is now completed
  const jobList = punch && punch.job_id && !jobs.some(j => j.id === punch.job_id)
    ? [{ id: punch.job_id, title: punch.job_title, job_number: punch.job_number, assigned: false }, ...jobs] : jobs;

  const spanMins = start && end && new Date(end) > new Date(start)
    ? Math.floor((new Date(end) - new Date(start)) / 60000) : null;
  const brkNum = Math.max(0, Math.round(Number(brk) || 0));
  const paid = spanMins !== null ? Math.max(0, spanMins - brkNum) / 60 : null;

  async function save() {
    setErr('');
    if (!punch && !worker) return setErr('Pick a worker');
    if (!start) return setErr('Enter a start time');
    if (!end && !isOpen) return setErr('Enter an end time');
    if (end && new Date(end) <= new Date(start)) return setErr('End time must be after start time');
    if (brkNum > maxBreak) return setErr(`Break cannot be more than ${fmtMins(maxBreak)}`);
    if (spanMins !== null && brkNum >= spanMins) return setErr('The break cannot be as long as the shift');
    setSaving(true);
    const body = {
      jobId,
      clockIn: new Date(start).toISOString(),
      clockOut: end ? new Date(end).toISOString() : null,
      breakMinutes: brkNum,
      note,
    };
    try {
      if (punch) await api.put(`/time/admin/punches/${punch.id}`, body);
      else await api.post('/time/admin/punches', { ...body, userId: worker });
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
        <div className="mhd">
          <div style={{ fontSize: 14, fontWeight: 600, flex: 1 }}>{punch ? 'Edit punch' : 'Add punch'}</div>
          <button className="btn btn-sm" style={{ padding: '4px 7px' }} onClick={onClose}><i className="fa fa-times"></i></button>
        </div>
        <div className="mbody">
          {!punch && (
            <div className="fg"><label className="fl">Worker</label>
              <select className="fs" value={worker} onChange={e => setWorker(e.target.value)} style={{ width: '100%' }}>
                <option value="">Select…</option>
                {employees.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
          )}
          <div className="fg"><label className="fl">Job</label><JobSelect jobs={jobList} value={jobId} onChange={setJobId} style={{ width: '100%' }} /></div>
          <div className="fgrid">
            <div className="fg"><label className="fl">Start</label><input type="datetime-local" className="fi" value={start} onChange={e => setStart(e.target.value)} /></div>
            <div className="fg"><label className="fl">End{isOpen ? ' (blank = still on clock)' : ''}</label><input type="datetime-local" className="fi" value={end} onChange={e => setEnd(e.target.value)} /></div>
          </div>
          <div className="fg">
            <label className="fl">Unpaid break (minutes)</label>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="number" min="0" className="fi" value={brk} onChange={e => setBrk(e.target.value)} style={{ width: 110 }} />
              {[0, 15, 30, 60].map(v => (
                <button key={v} className="btn btn-sm" onClick={() => setBrk(String(v))} style={{ color: 'var(--tx3)' }}>{v}</button>
              ))}
            </div>
          </div>
          {paid !== null && (
            <div style={{ fontSize: 11, color: 'var(--tx3)', marginTop: -4, marginBottom: 10 }}>
              {fmtHrs(spanMins / 60)} on site{brkNum > 0 ? ` − ${fmtMins(brkNum)} break` : ''} = <strong style={{ color: 'var(--tx2)' }}>{paid.toFixed(2)} hrs paid</strong>
            </div>
          )}
          <div className="fg"><label className="fl">Note</label><input className="fi" value={note} onChange={e => setNote(e.target.value)} placeholder="Optional — why it was changed" /></div>
          {err && <div style={{ fontSize: 12, color: 'var(--red2)', padding: '8px 10px', background: 'rgba(231,76,60,.1)', borderRadius: 'var(--r)' }}><i className="fa fa-exclamation-circle"></i> {err}</div>}
        </div>
        <div className="mfoot">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-blue" onClick={save} disabled={saving}>
            {saving ? <><div className="spin" style={{ width: 14, height: 14, borderWidth: 2 }}></div> Saving…</> : <><i className="fa fa-check"></i> Save</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── FIX REQUESTS ──────────────────────────────────────────────
function FixRequests({ onChanged }) {
  const [status, setStatus] = useState('pending');
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setRows(null);
    try { const r = await api.get('/time/admin/corrections', { params: { status } }); setRows(r.data); }
    catch (e) { setRows([]); }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  async function review(c, action) {
    let note = '';
    if (action === 'reject') {
      const n = window.prompt('Reason for rejecting (the worker will see this):', '');
      if (n === null) return;
      note = n;
    }
    setBusy(c.id);
    try {
      await api.put(`/time/admin/corrections/${c.id}`, { action, note });
      await load();
      onChanged && onChanged();
    } catch (e) {
      alert(e.response?.data?.error || 'Could not save');
    } finally {
      setBusy(null);
    }
  }

  const span = (a, b) => `${fmtDay(toLocalInput(a).slice(0, 10))} · ${fmtTime(a)} – ${fmtTime(b)}`;
  const paidHrs = (a, b, brk) => a && b ? Math.max(0, (new Date(b) - new Date(a)) / 60000 - (Number(brk) || 0)) / 60 : null;

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {['pending', 'approved', 'rejected'].map(s => (
          <button key={s} className={`btn btn-sm${status === s ? ' btn-blue' : ''}`} style={{ textTransform: 'capitalize' }} onClick={() => setStatus(s)}>{s}</button>
        ))}
      </div>
      {!rows && <div className="empty"><div className="spin" style={{ margin: '0 auto 12px' }}></div></div>}
      {rows && rows.length === 0 && <div className="empty"><i className="fa fa-check-circle" style={{ color: 'var(--green2)' }}></i>No {status} requests</div>}
      {rows && rows.map(c => {
        const newH = paidHrs(c.clock_in, c.clock_out, c.break_minutes);
        const oldH = c.punch_id ? paidHrs(c.orig_clock_in, c.orig_clock_out, c.orig_break_minutes) : null;
        const delta = oldH !== null && newH !== null ? newH - oldH : null;
        return (
          <div key={c.id} style={{ ...card, padding: '12px 14px', marginBottom: 8, borderLeft: `3px solid ${status === 'pending' ? 'var(--amber)' : status === 'approved' ? 'var(--green)' : 'var(--red)'}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: c.user_color || '#3A7BD5' }}></span>
              <span style={{ fontWeight: 600 }}>{c.user_name}</span>
              <span className="tag tag-gray" style={{ fontSize: 9 }}>{c.punch_id ? 'Fix punch' : 'Missed punch'}</span>
              {delta !== null && Math.abs(delta) >= 0.01 && (
                <span className={`tag tag-${delta > 0 ? 'amber' : 'gray'}`} style={{ fontSize: 9 }}>
                  {delta > 0 ? '+' : ''}{delta.toFixed(2)} hrs
                </span>
              )}
              <span style={{ flex: 1 }}></span>
              <span style={{ fontSize: 11, color: 'var(--tx3)' }}>Sent {new Date(c.created_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: c.punch_id ? '1fr 1fr' : '1fr', gap: 8, fontSize: 12, marginBottom: 8 }}>
              {c.punch_id && (
                <div style={{ background: 'var(--bg3)', borderRadius: 'var(--r)', padding: '8px 10px' }}>
                  <div style={{ fontSize: 10, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 3 }}>Was</div>
                  {c.orig_clock_in
                    ? <>{span(c.orig_clock_in, c.orig_clock_out)}{!c.orig_clock_out && ' (still open)'}
                        <div style={{ color: 'var(--tx3)' }}>
                          {jobLabel({ job_id: c.orig_job_id, job_title: c.orig_job_title, job_number: c.orig_job_number })}
                          {c.orig_break_minutes > 0 ? ` · ${fmtMins(c.orig_break_minutes)} break` : ''}
                          {oldH !== null ? ` · ${oldH.toFixed(2)} hrs` : ''}
                        </div></>
                    : <span style={{ color: 'var(--tx3)' }}>Original punch was deleted</span>}
                </div>
              )}
              <div style={{ background: 'rgba(58,123,213,.08)', borderRadius: 'var(--r)', padding: '8px 10px' }}>
                <div style={{ fontSize: 10, color: 'var(--blue2)', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 3 }}>{c.punch_id ? 'Change to' : 'Add'}</div>
                {span(c.clock_in, c.clock_out)}
                <div style={{ color: 'var(--tx3)' }}>
                  {jobLabel(c)}
                  {c.break_minutes > 0 ? ` · ${fmtMins(c.break_minutes)} break` : c.break_minutes === 0 ? ' · no break' : ''}
                  {newH !== null ? ` · ${newH.toFixed(2)} hrs` : ''}
                </div>
              </div>
            </div>
            <div style={{ fontSize: 12, color: 'var(--tx2)' }}>“{c.reason}”</div>
            {c.review_note && <div style={{ fontSize: 11, color: 'var(--tx3)', marginTop: 4 }}>Note: {c.review_note}</div>}
            {c.reviewer_name && <div style={{ fontSize: 11, color: 'var(--tx3)', marginTop: 2 }}>{status === 'approved' ? 'Approved' : 'Rejected'} by {c.reviewer_name}</div>}
            {status === 'pending' && (
              <div style={{ display: 'flex', gap: 7, marginTop: 10, justifyContent: 'flex-end' }}>
                <button className="btn btn-sm btn-red" disabled={busy === c.id} onClick={() => review(c, 'reject')}><i className="fa fa-times"></i> Reject</button>
                <button className="btn btn-sm btn-green" disabled={busy === c.id} onClick={() => review(c, 'approve')}><i className="fa fa-check"></i> Approve</button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── SETTINGS ──────────────────────────────────────────────────
function Settings({ health }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [anchorDraft, setAnchorDraft] = useState('');

  useEffect(() => {
    api.get('/time/admin/settings').then(r => { setS(r.data); setAnchorDraft(r.data.pay_period_anchor || ''); }).catch(() => setS({}));
  }, []);

  async function update(patch) {
    setSaving(true);
    try { const r = await api.put('/time/admin/settings', patch); setS(r.data); }
    catch (e) { alert(e.response?.data?.error || 'Could not save'); }
    finally { setSaving(false); }
  }

  if (!s) return <div className="empty"><div className="spin" style={{ margin: '0 auto 12px' }}></div></div>;

  const row = { ...card, padding: '14px 16px', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 12 };

  return (
    <div style={{ maxWidth: 600 }}>
      <div style={row}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600 }}><i className="fa fa-location-dot" style={{ color: 'var(--blue2)', marginRight: 6 }}></i>Record GPS location</div>
          <div style={{ fontSize: 12, color: 'var(--tx3)', marginTop: 3 }}>Capture where workers are when they clock in and out. Phones will ask permission the first time. Punches still go through if location is denied.</div>
        </div>
        <button className={`btn btn-sm${s.gps_enabled ? ' btn-green' : ''}`} disabled={saving} onClick={() => update({ gps_enabled: !s.gps_enabled })} style={{ minWidth: 56, justifyContent: 'center' }}>
          {s.gps_enabled ? 'On' : 'Off'}
        </button>
      </div>

      <div style={row}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600 }}><i className="fa fa-calendar-week" style={{ color: 'var(--blue2)', marginRight: 6 }}></i>Pay week starts on</div>
          <div style={{ fontSize: 12, color: 'var(--tx3)', marginTop: 3 }}>Sets the week used for timesheets, overtime and the CSV export.</div>
        </div>
        <select className="fs" value={Number(s.week_start)} disabled={saving} onChange={e => update({ week_start: Number(e.target.value) })}>
          <option value={1}>Monday</option>
          <option value={0}>Sunday</option>
        </select>
      </div>

      <div style={{ ...row, display: 'block' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600 }}><i className="fa fa-calendar-days" style={{ color: 'var(--blue2)', marginRight: 6 }}></i>Pay period length</div>
            <div style={{ fontSize: 12, color: 'var(--tx3)', marginTop: 3 }}>How much time the Timesheets tab shows at once. Overtime is still worked out week by week.</div>
          </div>
          <select className="fs" value={Number(s.pay_period_weeks) || 2} disabled={saving} onChange={e => update({ pay_period_weeks: Number(e.target.value) })}>
            {[1, 2, 3, 4].map(n => <option key={n} value={n}>{n} week{n > 1 ? 's' : ''}</option>)}
          </select>
        </div>
        {Number(s.pay_period_weeks) > 1 && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 3 }}>First day of a real pay period</div>
            <div style={{ fontSize: 12, color: 'var(--tx3)', marginBottom: 8 }}>
              Set this to the start date of any pay period you've actually run. Without it there's no way to know which weeks pair together, and the periods would sit a week off from your payroll calendar.
            </div>
            <div style={{ display: 'flex', gap: 7, alignItems: 'center' }}>
              <input type="date" className="fi" value={anchorDraft} onChange={e => setAnchorDraft(e.target.value)} style={{ width: 160 }} />
              <button className="btn btn-blue btn-sm" disabled={saving || !anchorDraft || anchorDraft === s.pay_period_anchor}
                onClick={() => update({ pay_period_anchor: anchorDraft })}>
                <i className="fa fa-check"></i> Save
              </button>
              <span style={{ fontSize: 11, color: 'var(--tx3)' }}>
                Currently {s.pay_period_anchor || 'not set'}
              </span>
            </div>
          </div>
        )}
      </div>

      <div style={{ ...row, display: 'block' }}>
        <div style={{ fontWeight: 600, marginBottom: 3 }}><i className="fa fa-mug-hot" style={{ color: 'var(--amber2)', marginRight: 6 }}></i>Unpaid breaks</div>
        <div style={{ fontSize: 12, color: 'var(--tx3)', marginBottom: 12 }}>
          Workers tap <b>Break</b> to pause the clock and <b>End break</b> to resume. If they never touch it, clock-out asks them for minutes. Break time comes off paid hours.
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 600 }}>Automatic deduction</div>
            <div style={{ fontSize: 12, color: 'var(--tx3)', marginTop: 2 }}>
              A backstop for when nothing was recorded at all. Set to 0 to leave it off — then only what a worker records (or you enter) is deducted.
            </div>
          </div>
          <select className="fs" value={Number(s.auto_break_minutes) || 0} disabled={saving} onChange={e => update({ auto_break_minutes: Number(e.target.value) })}>
            <option value={0}>Off</option>
            {[15, 30, 45, 60].map(n => <option key={n} value={n}>{n} min</option>)}
          </select>
        </div>

        {Number(s.auto_break_minutes) > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 600 }}>…on shifts longer than</div>
              <div style={{ fontSize: 12, color: 'var(--tx3)', marginTop: 2 }}>Shorter shifts get no automatic deduction.</div>
            </div>
            <select className="fs" value={Number(s.auto_break_after_hours) || 6} disabled={saving} onChange={e => update({ auto_break_after_hours: Number(e.target.value) })}>
              {[4, 5, 6, 7, 8].map(n => <option key={n} value={n}>{n} hours</option>)}
            </select>
          </div>
        )}
      </div>

      {health && (
        <div style={{ ...card, padding: '14px 16px', marginBottom: 10 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>
            <i className={`fa ${health.ok ? 'fa-circle-check' : 'fa-triangle-exclamation'}`}
               style={{ color: health.ok ? 'var(--green2)' : 'var(--red2)', marginRight: 6 }}></i>
            Clock check
          </div>
          <div style={{ fontSize: 12, color: 'var(--tx3)' }}>
            {health.ok
              ? <>Hours are being figured at a fixed {health.offsetInUse} — Manitoba time, no seasonal change.
                  Today is {health.node?.today}, current pay period {health.currentPeriod?.start} to {health.currentPeriod?.end}.</>
              : health.warning}
          </div>
          {health.note && (
            <div style={{ fontSize: 11, color: 'var(--tx3)', marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--border)' }}>
              <i className="fa fa-circle-info" style={{ marginRight: 5 }}></i>{health.note}
            </div>
          )}
        </div>
      )}

      <div style={{ ...card, padding: '14px 16px', fontSize: 12, color: 'var(--tx2)' }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Who uses the time clock</div>
        Anyone set to <b>In-house</b> with rate <b>Per hour</b> in Crews &amp; Subs. They get a Time Clock tab in the field app.
      </div>
    </div>
  );
}
